import instantsearch from 'instantsearch.js';
import {
  configure,
  searchBox,
  toggleRefinement,
  clearRefinements,
} from 'instantsearch.js/es/widgets';
import { history } from 'instantsearch.js/es/lib/routers';
import { connectRange, connectHits } from 'instantsearch.js/es/connectors';
import searchClient from '@pratt/js/components/algolia/search-client';
import { HitHelpers, MONTHS, TIME_ZONE } from '@pratt/js/components/algolia/hit-helpers';
import { customPagination } from '@pratt/js/components/algolia/pagination';
import { refinementListWithPanel } from '@pratt/js/components/algolia/panel';
import { refinementListTemplate } from '@pratt/js/components/algolia/templates/refinement-list';

function getDateET(year, month, day) {
  const date = new Date(Date.UTC(year, month, day));

  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone: TIME_ZONE }));
  const offset = utcDate.getTime() - tzDate.getTime();

  date.setTime(date.getTime() + offset);
  return date;
}

class EventSearch {
  constructor(rootEl) {
    this.rootEl = rootEl;
    if (!this.rootEl) {
      throw new Error('Root event search element not found.');
    }

    this.hitsPerPage = 24;

    this.indexName = this.rootEl.dataset['algoliaIndex'];
    this.minYear = Number.parseInt(this.rootEl.dataset['minYear'], 10);
    this.maxYear = Number.parseInt(this.rootEl.dataset['maxYear'], 10);

    this.search = instantsearch({
      indexName: this.indexName,
      searchClient: searchClient,
      routing: {
        router: history({
          cleanUrlOnDispose: true,
        }),
      },
      insights: true,
      future: {
        preserveSharedStateOnUnmount: true,
      },
    });

    // If URL has query params, use those as starting range. Otherwise get today's month & year.
    let startDate = new Date();
    if (window.location.search) {
      const urlParams = new URLSearchParams(window.location.search);
      const params = Object.fromEntries(urlParams.entries());
      const startParam = params[`${this.indexName}[range][occurrences.start]`];
      if (startParam) {
        startDate = new Date(Number(startParam.replace(':', '')));
      }
    }
    this.month = startDate.getMonth();
    this.year = startDate.getFullYear();

    this.customHits = connectHits(this.renderHits.bind(this));

    this.init();
    this.search.start();
  }

  init() {
    this.search.addWidgets([
      // https://www.algolia.com/doc/api-reference/widgets/configure/js/
      // Parameter options: https://www.algolia.com/doc/api-reference/search-api-parameters/
      configure({
        hitsPerPage: this.hitsPerPage,
        distinct: true,
        // Force faceting to be applied after de-duplication (via the Distinct setting)
        facetingAfterDistinct: true,
        // Filter results by 'type' facet
        // https://www.algolia.com/doc/api-reference/api-parameters/filters/#facet-filters
        filters: `type:"event"`,
      }),

      this.dateInput({
        onRefine: () => {
          // Earliest date is first day of month at 12:00:00AM
          const currentDate = new Date(
            new Date().getUTCFullYear(),
            new Date().getUTCMonth(),
            new Date().getUTCDate(),
          );
          const currentMonthYear = new Date(
            new Date(currentDate).getUTCFullYear(),
            new Date(currentDate).getUTCMonth(),
            1,
          );
          const setMonthYear = new Date(new Date(this.year), new Date(this.month), 1);

          let earliestDate = getDateET(this.year, this.month, 1);

          if (currentMonthYear.getTime() === setMonthYear.getTime()) {
            earliestDate = getDateET(
              new Date(currentDate).getUTCFullYear(),
              new Date(currentDate).getUTCMonth(),
              new Date().getUTCDate(),
            );
          }

          return [earliestDate.getTime(), undefined];
        },
      })({
        attribute: 'occurrences.end',
      }),

      this.dateInput({
        onRefine: () => {
          // Latest date is last day of month at 11:59:59PM
          const latestDate = getDateET(this.year, this.month + 1, 1);
          latestDate.setTime(latestDate.getTime() - 1000);
          return [undefined, latestDate.getTime()];
        },
      })({
        attribute: 'occurrences.start',
      }),

      searchBox({
        container: '.js-event-search-input',
        placeholder: 'Search',
        showSubmit: false,
      }),

      clearRefinements({
        container: '.js-event-search-clear',
        templates: {
          resetLabel: 'Reset',
        },
        excludedAttributes: ['occurrences.start', 'occurrences.end'],
      }),

      refinementListWithPanel('School')({
        container: '.js-event-search-schools-input',
        attribute: 'schools',
        operator: 'and',
        limit: 10,
        showMore: true,
        showMoreLimit: 100,
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      refinementListWithPanel('Department')({
        container: '.js-event-search-departments-input',
        attribute: 'departments',
        operator: 'and',
        limit: 10,
        showMore: true,
        showMoreLimit: 100,
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      refinementListWithPanel('Office')({
        container: '.js-event-search-offices-input',
        attribute: 'offices',
        operator: 'and',
        limit: 10,
        showMore: true,
        showMoreLimit: 100,
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      refinementListWithPanel('Special Categories')({
        container: '.js-event-search-special-events-input',
        attribute: 'events_cats',
        operator: 'and',
        limit: 10,
        showMore: true,
        showMoreLimit: 100,
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      toggleRefinement({
        container: '.js-event-search-toggle-input',
        attribute: 'is_internal',
        templates: {
          labelText: 'Internal Only',
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/hits/js/
      this.customHits({
        container: '.js-event-search-hits',
        templates: {
          /* eslint-disable */
          item: hit => {
            // prettier-ignore
            return `
              <div class="event-hit${hit.thumbnail ? ` event-hit--has-image` : ''}${hit.is_featured || hit.feature_level === 'large_feature' ? ' event-hit--featured' : hit.feature_level === 'small_feature' ? ' event-hit--featured-small' : ' event-hit--featured-small'}${hit.is_past ? ' event-hit--past' : ''}">
                <div class="event-hit-inner ${(hit.palette === 'secondary' && (hit.is_featured || hit.feature_level === 'large_feature' || hit.feature_level === 'small_feature')) ? `theme--${hit.colorway_secondary}` : (hit.palette === 'tertiary' && (hit.is_featured || hit.feature_level === 'large_feature' || hit.feature_level === 'small_feature')) ? `theme--${hit.colorway_tertiary}` : (hit.palette === 'special' && (hit.is_featured || hit.feature_level === 'large_feature' || hit.feature_level === 'small_feature')) ? `theme--${hit.colorway_special}` : 'theme--white'}">

                  ${HitHelpers.Events.dateAsHTML(hit)}

                  <div class="event-hit__content">

                    <div class="event-hit__titles">
                      ${hit.is_internal ? '<p class="event-hit__internal">Internal</p>' : ''}
                      <p class="event-hit__title">
                        <a class="event-hit__link" href="${hit.url}" onClick="sendEvent('click','${hit.type} clicked', '${hit.objectID}', '${hit.__queryID}', ${hit.__position})">
                          <span class="event-hit__title-text">${hit.title}</span>
                        </a>
                      </p>
                    </div>

                    <div class="event-hit__meta">
                      <p class="event-hit__time">
                        ${HitHelpers.Events.time(hit)}
                      </p>
                      ${hit.location ? `
                        <p class="event-hit__location">
                          ${HitHelpers.Events.location(hit)}
                        </p>
                      ` : ''}
                    </div>

                  </div>

                  ${hit.thumbnail ? `
                    <div class="event-hit__image">
                      <img src="${hit.thumbnail}" alt="${hit.title}" />
                    </div>
                  ` : ''}
                </div>
              </div>
            `;
          },
          /* eslint-enable */
        },
      }),

      customPagination({
        container: document.querySelector('.js-event-search-pagination'),
      }),
    ]);
  }

  dateInput({ onRefine }) {
    return connectRange(({ refine }, isFirstRender) => {
      const container = document.querySelector('.js-event-search-date-input');
      let monthSelect = container.querySelector('select#month-input');
      let yearSelect = container.querySelector('select#year-input');

      if (isFirstRender) {
        if (!monthSelect) {
          const monthContainer = container.querySelector('.js-event-search-date-month-input');
          monthSelect = document.createElement('select');
          monthSelect.id = 'month-input';
          MONTHS.forEach((month, index) => {
            const option = document.createElement('option');
            option.value = index;
            option.innerText = month;
            monthSelect.appendChild(option);
          });
          monthSelect.value = this.month;
          monthContainer.appendChild(monthSelect);
        }

        monthSelect.addEventListener('change', event => {
          this.month = Number.parseInt(event.target.value, 10);
          refine(onRefine());
        });

        if (!yearSelect) {
          const yearContainer = container.querySelector('.js-event-search-date-year-input');
          yearSelect = document.createElement('select');
          yearSelect.id = 'year-input';
          const years = [];
          // Add the year options. Have it start one year before the current year.
          for (let y = this.minYear - 1; y <= this.maxYear; y++) {
            years.push(y);
          }
          years.forEach(year => {
            const option = document.createElement('option');
            option.value = year;
            option.innerText = year;
            yearSelect.appendChild(option);
          });
          yearSelect.value = this.year;
          yearContainer.appendChild(yearSelect);
        }

        yearSelect.addEventListener('change', event => {
          this.year = Number.parseInt(event.target.value, 10);
          refine(onRefine());
        });

        refine(onRefine());
      }
    });
  }

  renderHits(renderOpts) {
    const { widgetParams } = renderOpts;

    const container = document.querySelector(widgetParams.container);

    // Earliest date is first day of month at 12:00:00AM
    const earliestDate = getDateET(this.year, this.month, 1);
    // Latest date is last day of month at 11:59:59PM
    const latestDate = getDateET(this.year, this.month + 1, 1);
    latestDate.setTime(latestDate.getTime() - 1000);

    const allIndividualEvents = renderOpts.hits.flatMap(event => {
      const individualEvents = [];
      event.occurrences.forEach(occurrence => {
        // Only get the occurrences that match the current filter
        if (occurrence.end >= earliestDate.getTime() && occurrence.start <= latestDate.getTime()) {
          individualEvents.push({
            ...event,
            occurrences: [occurrence],
          });
        }
      });
      return individualEvents;
    });

    const currentDate = new Date(
      new Date().getUTCFullYear(),
      new Date().getUTCMonth(),
      new Date().getUTCDate(),
    );
    const currentEvents = [];
    const pastEvents = [];

    allIndividualEvents
      .sort((a, b) => a.occurrences[0].start - b.occurrences[0].start)
      .forEach(event => {
        event.is_past = event.occurrences[0].end < currentDate;
        if (event.is_past) {
          pastEvents.push(event);
        } else {
          currentEvents.push(event);
        }
      });

    if (!allIndividualEvents.length) {
      container.innerHTML = `<p class="ais-Hits--empty">No events</p>`;
    } else {
      /* eslint-disable */
      // prettier-ignore
      container.innerHTML = `
        ${currentEvents.length ? `
          <h3 class="hidden">Ongoing and Future Events</h3>
          <ol class="ais-Hits-list">
            ${currentEvents.map(event => `
              <li class="ais-Hits-item">${widgetParams.templates.item(event)}</li>
            `).join('\n')}
          </ol>
        `: ''}

        ${pastEvents.length ? `
          <h3 class="hidden">Past Events</h3>
          <ol class="ais-Hits-list">
            ${pastEvents.map(event => `
              <li class="ais-Hits-item">${widgetParams.templates.item(event)}</li>
            `).join('\n')}
          </ol>
        `: ''}
      `;
      /* eslint-enable */
    }
  }
}

export default EventSearch;
